<template>
  <div ref="newUserModalRef" class="new-user-modal">
    <artmisads-modal :open="open" :width="1200" :closable="true" :getContainer="() => $refs.newUserModalRef"
      @cancel="() => emit('hide')" :footer="null">
      <div class="new-user-cont">
        <div class="bg">
          <img class="bg1" alt="artemisAds" src="../assets/images/new-user-modal/new_user_modal_bg1.png" />
          <img class="bg2" alt="artemisAds" src="../assets/images/new-user-modal/new_user_modal_bg2.png" />
        </div>
        <div class="cont">
          <div class="head">
            <p class="title">{{ t('seller.contactUsModal.title') }}</p>
          </div>
          <div class="main-cont">
            <p class="intro">{{ t('seller.contactUsModal.intro') }}</p>
            <div class="contact-wrapper">
              <div class="qrlist">
                <div class="qr-item" v-for="item in qrChannels" :key="item.id">
                  <div class="logo">
                    <svg v-if="item.useSvg" :class="'icon ' + item.id.toLowerCase()"
                      aria-hidden="true" style="fill: #fff">
                      <use :xlink:href="'#icon-' + item.id.toLowerCase()" />
                    </svg>
                    <i v-else :class="'iconfont icon-' + item.id.toLowerCase()" />{{ channelLabel(item.id) }}
                  </div>
                  <div class="qrcode">
                    <img v-if="item.id == 'Slack'" src="../assets/images/social-media-qrcode/slack.png" :alt="item.id" />
                    <img v-else-if="item.id == 'WhatsApp'" src="../assets/images/social-media-qrcode/whatsapp.png"
                      :alt="item.id" />
                    <img v-else-if="item.id == 'Wechat'" :src="wechatQrSrc" :alt="item.id" />
                    <img v-else-if="item.id == 'Lark'" src="../assets/images/social-media-qrcode/lark.png" :alt="item.id" />
                    <p>{{ t('seller.contactUsModal.scanQrCode') }}</p>
                  </div>
                </div>
              </div>
              <p>{{ t('seller.contactUsModal.emailLine') }}</p>
            </div>
          </div>
        </div>
        <div class="btn">
          <artmisads-button type="primary" @click="() => emit('hide')">{{ t('seller.contactUsModal.btnClose') }}</artmisads-button>
        </div>
      </div>
    </artmisads-modal>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import wechatSellerQr from '../assets/images/social-media-qrcode/wechatSeller.png'
import wechatPublisherQr from '../assets/images/social-media-qrcode/wechatPublisher.png'

/** 必须使用 global scope，否则子组件在生产环境可能拿不到根应用合并后的 seller 文案 */
const { t } = useI18n({ useScope: 'global' })

const { open, userType = 'seller' } = defineProps(['open', 'userType'])
const emit = defineEmits(['hide'])

const wechatQrSrc = computed(() =>
  userType === 'publisher' ? wechatPublisherQr : wechatSellerQr,
)

const qrChannels = [
  { id: 'Slack', useSvg: true },
  { id: 'WhatsApp', useSvg: false },
  { id: 'Wechat', useSvg: false },
  { id: 'Lark', useSvg: true },
]

/** 避免动态拼接 key，利于打包与运行时解析稳定 */
const CONTACT_CHANNEL_KEYS = {
  Slack: 'seller.contactUsModal.channelSlack',
  WhatsApp: 'seller.contactUsModal.channelWhatsApp',
  Wechat: 'seller.contactUsModal.channelWechat',
  Lark: 'seller.contactUsModal.channelLark',
}

function channelLabel(id) {
  const key = CONTACT_CHANNEL_KEYS[id]
  return key ? t(key) : id
}
</script>

<style lang="less" scoped>
.new-user-modal {
  :deep(.ant-modal) {
    .ant-modal-body {
      padding: 0;
    }
  }

  .new-user-cont {
    position: relative;
    background-image: linear-gradient(170deg, #E2D9FC 0%, #FCF0FA 16%, #FBFAFF 41%, #FBFBFE 100%);
    box-shadow: 0 0 2px 0 rgba(0, 0, 0, 0.06), 0 16px 48px 0 rgba(0, 0, 0, 0.1), inset 2px 2px 0 0 rgba(255, 255, 255, 0.6);
    border-radius: 8px;

    .bg {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      display: flex;
      justify-content: space-between;
      z-index: 0;

      .bg1 {
        width: 324px;
        height: 247px;
      }

      .bg2 {
        width: 125px;
        height: 125px;
        margin-right: 49px;
        margin-top: 9px;
      }
    }
  }

  .cont {
    padding: 40px 40px 0 40px;
  }

  .head {
    .title {
      color: var(--dark-1);
      font-size: 32px;
      font-weight: 600;
      line-height: 40px;
      letter-spacing: 0.69px;
      text-align: left;
    }

    .text {
      font-size: 16px;
      margin-top: 5px;
      font-weight: 400;
      line-height: 24px;
      color: var(--dark-2);
      letter-spacing: 0.4px;
    }
  }

  .step-wrap {
    margin-top: 12px;
    margin-bottom: 32px;
  }

  .btn {
    display: flex;
    margin: 0 auto;
    align-items: center;
    padding: 32px 0 40px;
    justify-content: center;

    >button {
      width: 224px;
    }
  }
}

.main-cont {
  z-index: 1;
  margin-top: 24px;
  position: relative;
  background: #fff;
  border-radius: 8px;
  padding: 40px 80px 0;

  >p {
    font-size: 24px;
    font-weight: 600;
    text-align: center;
    color: var(--dark-1);

    &.intro {
      white-space: pre-line;
    }
  }

  .contact-wrapper {
    border-radius: 8px;
    margin: 24px 96px 0;
    padding: 30px 48px 0;
    background: var(--bg-gray-8);

    >p {
      padding: 24px 0;
    }
  }
}

.qrlist {
  gap: 48px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;

  .qr-item {
    gap: 16px;
    display: flex;
    align-items: center;
    flex-direction: column;

    .logo {
      gap: 6px;
      display: flex;
      font-size: 16px;
      font-weight: 600;
      align-items: center;
      color: var(--dark-3);
      letter-spacing: 0.4px;
      align-self: flex-start;

      >.icon,
      >.iconfont {
        width: 32px;
        height: 32px;
        padding: 4px;
        border-radius: 8px;
        background-color: #fff;
        border: 1px solid #E8E5ED;
      }

      >.iconfont {
        color: #fff;
        font-size: 24px;
        font-weight: normal;
        background-color: #25D366;

        &::before {
          display: block;
          margin-top: -7px;
          margin-left: -1px;
        }
      }
    }

    .qrcode {
      padding: 10px;
      text-align: center;
      border-radius: 10px;
      background-color: #fff;
      box-shadow: 0 1px 0 0 var(--section-box-shadow-2);

      >img {
        width: 112px;
        height: 112px;
      }

      >p {
        font-size: 11px;
        margin-top: 7px;
        color: var(--dark-3);
        letter-spacing: 0.28px;
      }
    }
  }
}
</style>
